import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Toaster, toast } from 'sonner';
import { 
  BookOpen, 
  Sun, 
  Moon, 
  LogOut, 
  Package, 
  Truck,
  Plus,
  ChevronLeft,
  ArrowLeft,
  CheckCircle,
  Clock,
  Send,
  Home as HomeIcon,
  Loader2
} from 'lucide-react';
import { formatCurrency, getStatusConfig } from '../lib/utils';
import { Link, useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function Shipments() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAddItemsModal, setShowAddItemsModal] = useState(false);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [preppedAcquisitions, setPreppedAcquisitions] = useState([]);
  const [selectedAcquisitions, setSelectedAcquisitions] = useState([]);
  
  // New shipment form
  const [newShipmentName, setNewShipmentName] = useState('');
  const [newShipmentMarketplace, setNewShipmentMarketplace] = useState('UK');

  const fetchShipments = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/shipments`, {
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        setShipments(data.shipments);
      }
    } catch (error) {
      console.error('Failed to fetch shipments:', error);
      toast.error('Failed to load shipments');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPreppedAcquisitions = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/acquisitions?status=PREPPED`, {
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        setPreppedAcquisitions(data.acquisitions);
      }
    } catch (error) {
      console.error('Failed to fetch acquisitions:', error);
    }
  }, []);

  useEffect(() => {
    fetchShipments();
  }, [fetchShipments]);

  const createShipment = async () => {
    if (!newShipmentName.trim()) {
      toast.error('Please enter a shipment name');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/shipments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          shipment_name: newShipmentName,
          marketplace: newShipmentMarketplace,
        }),
      });

      if (response.ok) {
        toast.success('Shipment created');
        setShowCreateModal(false);
        setNewShipmentName('');
        fetchShipments();
      }
    } catch (error) {
      toast.error('Failed to create shipment');
    }
  };

  const openAddItemsModal = async (shipment) => {
    setSelectedShipment(shipment);
    await fetchPreppedAcquisitions();
    setSelectedAcquisitions([]);
    setShowAddItemsModal(true);
  };

  const addItemsToShipment = async () => {
    if (selectedAcquisitions.length === 0) {
      toast.error('Please select items to add');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/shipments/${selectedShipment.shipment_id}/items`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(selectedAcquisitions),
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Added ${data.added.length} items to shipment`);
        setShowAddItemsModal(false);
        fetchShipments();
      }
    } catch (error) {
      toast.error('Failed to add items');
    }
  };

  const updateShipmentStatus = async (shipmentId, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/shipments/${shipmentId}?status=${newStatus}`, {
        method: 'PUT',
        credentials: 'include',
      });

      if (response.ok) {
        toast.success(`Shipment marked as ${newStatus}`);
        fetchShipments();
      }
    } catch (error) {
      toast.error('Failed to update shipment');
    }
  };

  const getShipmentStatusIcon = (status) => {
    switch (status) {
      case 'DRAFT': return <Clock className="h-4 w-4" />;
      case 'READY': return <Package className="h-4 w-4" />;
      case 'SENT': return <Send className="h-4 w-4" />;
      case 'RECEIVED': return <HomeIcon className="h-4 w-4" />;
      case 'LIVE': return <CheckCircle className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  const getShipmentStatusColor = (status) => {
    switch (status) {
      case 'DRAFT': return 'bg-zinc-500/10 text-zinc-600 dark:text-zinc-400';
      case 'READY': return 'bg-blue-500/10 text-blue-600 dark:text-blue-400';
      case 'SENT': return 'bg-orange-500/10 text-orange-600 dark:text-orange-400';
      case 'RECEIVED': return 'bg-purple-500/10 text-purple-600 dark:text-purple-400';
      case 'LIVE': return 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400';
      default: return 'bg-zinc-500/10 text-zinc-600';
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <header className="sticky top-0 z-40 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Truck className="h-7 w-7 text-gold" />
              <span className="font-heading text-lg font-bold">Shipment Builder</span>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
            >
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
            <Button variant="ghost" size="icon" onClick={logout}>
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-muted-foreground">Total Shipments</div>
              <div className="text-2xl font-bold font-mono">{shipments.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-muted-foreground">Active</div>
              <div className="text-2xl font-bold font-mono text-blue-500">
                {shipments.filter(s => ['DRAFT', 'READY', 'SENT'].includes(s.status)).length}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-muted-foreground">Total Units</div>
              <div className="text-2xl font-bold font-mono">
                {shipments.reduce((acc, s) => acc + (s.total_units || 0), 0)}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-muted-foreground">Expected Profit</div>
              <div className="text-2xl font-bold font-mono text-profit">
                {formatCurrency(shipments.reduce((acc, s) => acc + (s.total_estimated_profit || 0), 0))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="font-heading text-xl font-bold">Your Shipments</h2>
          <Button onClick={() => setShowCreateModal(true)} className="btn-gold gap-2" data-testid="create-shipment-btn">
            <Plus className="h-4 w-4" />
            New Shipment
          </Button>
        </div>

        {/* Shipments List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-gold" />
          </div>
        ) : shipments.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Truck className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-heading text-lg font-bold mb-2">No Shipments Yet</h3>
              <p className="text-muted-foreground mb-4">
                Create your first shipment to start tracking FBA inventory.
              </p>
              <Button onClick={() => setShowCreateModal(true)} className="btn-gold">
                Create First Shipment
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {shipments.map((shipment) => (
              <Card key={shipment.shipment_id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-heading text-lg font-bold">{shipment.shipment_name}</h3>
                        <Badge className={`gap-1 ${getShipmentStatusColor(shipment.status)}`}>
                          {getShipmentStatusIcon(shipment.status)}
                          {shipment.status}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-6 text-sm text-muted-foreground">
                        <span>Marketplace: {shipment.marketplace}</span>
                        <span>Units: {shipment.total_units}</span>
                        <span className="font-mono text-profit">
                          Est. Profit: {formatCurrency(shipment.total_estimated_profit)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {shipment.status === 'DRAFT' && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openAddItemsModal(shipment)}
                          >
                            Add Items
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => updateShipmentStatus(shipment.shipment_id, 'READY')}
                          >
                            Mark Ready
                          </Button>
                        </>
                      )}
                      {shipment.status === 'READY' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => updateShipmentStatus(shipment.shipment_id, 'SENT')}
                        >
                          Mark Sent
                        </Button>
                      )}
                      {shipment.status === 'SENT' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => updateShipmentStatus(shipment.shipment_id, 'RECEIVED')}
                        >
                          Mark Received
                        </Button>
                      )}
                      {shipment.status === 'RECEIVED' && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-emerald-600"
                          onClick={() => updateShipmentStatus(shipment.shipment_id, 'LIVE')}
                        >
                          Mark Live
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Create Shipment Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5 text-gold" />
              Create New Shipment
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Shipment Name *</label>
              <Input
                placeholder="e.g., January Batch 1"
                value={newShipmentName}
                onChange={(e) => setNewShipmentName(e.target.value)}
                data-testid="shipment-name-input"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Marketplace</label>
              <Select value={newShipmentMarketplace} onValueChange={setNewShipmentMarketplace}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="UK">🇬🇧 UK</SelectItem>
                  <SelectItem value="US">🇺🇸 US</SelectItem>
                  <SelectItem value="EU">🇪🇺 EU</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button className="btn-gold" onClick={createShipment} data-testid="create-shipment-submit">
                Create Shipment
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Items Modal */}
      <Dialog open={showAddItemsModal} onOpenChange={setShowAddItemsModal}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-gold" />
              Add Items to Shipment
            </DialogTitle>
          </DialogHeader>

          <div className="pt-4">
            {preppedAcquisitions.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Package className="h-10 w-10 mx-auto mb-3 opacity-50" />
                <p>No prepped items available</p>
                <p className="text-sm">Items must be in "PREPPED" status to add to shipments</p>
              </div>
            ) : (
              <>
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {preppedAcquisitions.map((acq) => (
                    <div
                      key={acq.acquisition_id}
                      className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedAcquisitions.includes(acq.acquisition_id)
                          ? 'border-gold bg-gold/5'
                          : 'hover:border-gold/50'
                      }`}
                      onClick={() => {
                        setSelectedAcquisitions((prev) =>
                          prev.includes(acq.acquisition_id)
                            ? prev.filter((id) => id !== acq.acquisition_id)
                            : [...prev, acq.acquisition_id]
                        );
                      }}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="font-medium">{acq.book?.title || 'Unknown Book'}</div>
                          <div className="text-sm text-muted-foreground">
                            Supplier: {acq.supplier_name} • {formatCurrency(acq.purchase_price)}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-mono text-profit">
                            {formatCurrency(acq.expected_profit)}
                          </div>
                          <div className="text-xs text-muted-foreground">Expected Profit</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between items-center pt-4 border-t mt-4">
                  <div className="text-sm text-muted-foreground">
                    {selectedAcquisitions.length} items selected
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setShowAddItemsModal(false)}>
                      Cancel
                    </Button>
                    <Button 
                      className="btn-gold" 
                      onClick={addItemsToShipment}
                      disabled={selectedAcquisitions.length === 0}
                    >
                      Add to Shipment
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
